import { useCallback, useEffect, useState } from 'react';
import inboxService, { type InboxMenuItem } from '../services/inboxService';

/** Documentos clínicos enviados pelo WhatsApp com escolha de inbox (Cloud API / Z-API). */
export type ClinicalDocumentWhatsAppKind = 'prescription' | 'certificate' | 'exam_order' | 'asaas_charge';

export interface PrescriptionWhatsAppInbox {
  channelId: string;
  name: string;
  provider: 'cloud_api' | 'zapi';
}

const STORAGE_PREFIX = 'klinvia.document_whatsapp_inbox';
/** Chave legada (só receituário) — migrada na leitura. */
const LEGACY_PRESCRIPTION_PREFIX = 'klinvia.prescription_whatsapp_inbox';

function currentUnitId(): string {
  try {
    const raw = localStorage.getItem('auth_user');
    return raw ? String(JSON.parse(raw)?.current_unit?.id || '') : '';
  } catch {
    return '';
  }
}

function storageKey(kind: ClinicalDocumentWhatsAppKind): string {
  return `${STORAGE_PREFIX}:${kind}:${currentUnitId() || 'default'}`;
}

function changeEventName(kind: ClinicalDocumentWhatsAppKind): string {
  return `klinvia:document-whatsapp-inbox-changed:${kind}`;
}

export function getClinicalDocumentWhatsAppInboxChannelId(
  kind: ClinicalDocumentWhatsAppKind,
): string {
  try {
    const key = storageKey(kind);
    const direct = localStorage.getItem(key);
    if (direct) return direct;
    if (kind === 'prescription') {
      const legacyKey = `${LEGACY_PRESCRIPTION_PREFIX}:${currentUnitId() || 'default'}`;
      const legacy = localStorage.getItem(legacyKey);
      if (legacy) {
        localStorage.setItem(key, legacy);
        localStorage.removeItem(legacyKey);
        return legacy;
      }
    }
    return '';
  } catch {
    return '';
  }
}

/** @deprecated Use getClinicalDocumentWhatsAppInboxChannelId('prescription') */
export function getPrescriptionWhatsAppInboxChannelId(): string {
  return getClinicalDocumentWhatsAppInboxChannelId('prescription');
}

/** Vazio = automático (instância padrão da unidade). */
export function setClinicalDocumentWhatsAppInboxChannelId(
  kind: ClinicalDocumentWhatsAppKind,
  channelId: string,
): void {
  try {
    const key = storageKey(kind);
    if (channelId) localStorage.setItem(key, channelId);
    else localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(changeEventName(kind), { detail: channelId }));
}

export function setPrescriptionWhatsAppInboxChannelId(channelId: string): void {
  setClinicalDocumentWhatsAppInboxChannelId('prescription', channelId);
}

function toSendableInbox(item: InboxMenuItem): PrescriptionWhatsAppInbox | null {
  if (!item.channelId) return null;
  const provider = String(item.provider || '').toLowerCase().replace(/-/g, '');
  if (provider === 'cloud_api' || provider === 'cloudapi') {
    return { channelId: item.channelId, name: item.text, provider: 'cloud_api' };
  }
  if (provider === 'zapi') {
    return { channelId: item.channelId, name: item.text, provider: 'zapi' };
  }
  return null;
}

export const PRESCRIPTION_INBOX_PROVIDER_LABEL: Record<PrescriptionWhatsAppInbox['provider'], string> = {
  cloud_api: 'API oficial',
  zapi: 'Z-API',
};

export const CLINICAL_DOCUMENT_WHATSAPP_COPY: Record<
  ClinicalDocumentWhatsAppKind,
  { pageTooltip: string; cloudApiHint: string; zapiHint: string }
> = {
  prescription: {
    pageTooltip: 'Inbox que envia os receituários pelo WhatsApp (aqui e no histórico do paciente)',
    cloudApiHint: 'API oficial · template de receituário aprovado',
    zapiHint: 'Z-API · envia o PDF',
  },
  certificate: {
    pageTooltip: 'Inbox que envia os atestados pelo WhatsApp (aqui e ao compartilhar com o paciente)',
    cloudApiHint: 'API oficial · template de documento aprovado (link no botão)',
    zapiHint: 'Z-API · envia o PDF',
  },
  exam_order: {
    pageTooltip: 'Inbox que envia os pedidos de exame pelo WhatsApp (aqui e ao compartilhar com o paciente)',
    cloudApiHint: 'API oficial · template de documento aprovado (link no botão)',
    zapiHint: 'Z-API · envia o PDF',
  },  asaas_charge: {
    pageTooltip: 'Inbox que envia as cobranças Asaas ao paciente',
    cloudApiHint: 'API oficial · fora da janela de 24h usa o template de cobrança',
    zapiHint: 'Z-API · link, QR Code e PIX copia e cola',
  },
};

/** Lista de inboxes Cloud API / Z-API da unidade + escolha salva por tipo de documento. */
export function useClinicalDocumentWhatsAppInbox(
  kind: ClinicalDocumentWhatsAppKind,
  enabled = true,
) {
  const [inboxes, setInboxes] = useState<PrescriptionWhatsAppInbox[]>([]);
  const [loading, setLoading] = useState(false);
  const [channelId, setChannelIdState] = useState<string>(() =>
    getClinicalDocumentWhatsAppInboxChannelId(kind),
  );

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    setChannelIdState(getClinicalDocumentWhatsAppInboxChannelId(kind));
    inboxService
      .getMenuInboxes()
      .then((menu) => {
        if (cancelled) return;
        const seen = new Set<string>();
        const list = (menu?.all || [])
          .map(toSendableInbox)
          .filter((inbox): inbox is PrescriptionWhatsAppInbox => {
            if (!inbox || seen.has(inbox.channelId)) return false;
            seen.add(inbox.channelId);
            return true;
          });
        setInboxes(list);
      })
      .catch(() => {
        if (!cancelled) setInboxes([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, kind]);

  useEffect(() => {
    const onChange = (event: Event) => {
      setChannelIdState(String((event as CustomEvent).detail || ''));
    };
    window.addEventListener(changeEventName(kind), onChange);
    return () => window.removeEventListener(changeEventName(kind), onChange);
  }, [kind]);

  const setChannelId = useCallback(
    (next: string) => {
      setChannelIdState(next);
      setClinicalDocumentWhatsAppInboxChannelId(kind, next);
    },
    [kind],
  );

  const selected = inboxes.find((inbox) => inbox.channelId === channelId) || null;
  const effectiveChannelId = !loading && channelId && !selected ? '' : channelId;

  return { inboxes, loading, channelId: effectiveChannelId, selected, setChannelId };
}

export function usePrescriptionWhatsAppInbox(enabled = true) {
  return useClinicalDocumentWhatsAppInbox('prescription', enabled);
}
